'use client';

import { Check, Music, Pause, Play, Search, Upload, X } from 'lucide-react';
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useAudioUpload } from './hooks/useAudioUpload';
import { MUSIC_LIBRARY } from '@/lib/editor/musicLibrary';
import type { EditorAudioTrack } from '@/types/editor';

const AUDIO_CATEGORIES = ['Все', 'Поп', 'Фоновая', 'Комедия', 'Драйв', 'Интрига'] as const;

interface Props {
  authedFetch: typeof fetch;
  audioTrack: EditorAudioTrack | null;
  audioTracks: EditorAudioTrack[];
  onAdd: (track: EditorAudioTrack) => void;
  onSelect: (trackId: string) => void;
  onRemove: (trackId: string) => void;
  t: (key: string) => string;
}

function formatSeconds(s?: number): string {
  if (!s || !Number.isFinite(s)) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export function AudioPanel({
  authedFetch,
  audioTrack,
  audioTracks,
  onAdd,
  onSelect,
  onRemove,
  t,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, uploading, error } = useAudioUpload(authedFetch);

  // Single shared <audio> element for auditioning library tracks — reused
  // across tracks so starting one playback stops any other (no overlapping
  // preview audio), and is fully separate from the applied `audioTrack`.
  const auditionRef = useRef<HTMLAudioElement>(null);
  const [auditioningId, setAuditioningId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<(typeof AUDIO_CATEGORIES)[number]>('Все');
  const filteredTracks = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ru');
    return MUSIC_LIBRARY.filter((track) => {
      const categoryMatches = category === 'Все' || track.category === category;
      const queryMatches = !needle || `${track.title} ${track.mood} ${track.category}`.toLocaleLowerCase('ru').includes(needle);
      return categoryMatches && queryMatches;
    });
  }, [category, query]);

  useEffect(() => {
    // Stop auditioning when the panel unmounts (e.g. switching tabs).
    const el = auditionRef.current;
    return () => {
      el?.pause();
    };
  }, []);

  const toggleAudition = (track: (typeof MUSIC_LIBRARY)[number]) => {
    const el = auditionRef.current;
    if (!el) return;
    if (auditioningId === track.id) {
      el.pause();
      setAuditioningId(null);
      return;
    }
    el.src = track.src;
    el.currentTime = 0;
    el.play().catch(() => {});
    setAuditioningId(track.id);
  };

  const applyLibraryTrack = (track: (typeof MUSIC_LIBRARY)[number]) => {
    auditionRef.current?.pause();
    setAuditioningId(null);
    onAdd({
      src: track.src,
      volume: 0.5,
      fadeIn: 0,
      fadeOut: 0,
      fileName: track.title,
      duration: track.duration,
      ducking: false,
    });
  };

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const result = await upload(file);
    if (result) {
      onAdd({
        s3Key: result.s3Key,
        src: result.url, // public URL — lets the editor preview play it live
        volume: 0.5,
        fadeIn: 0,
        fadeOut: 0,
        fileName: result.fileName,
        duration: result.duration,
        ducking: false,
      });
    }
    e.target.value = '';
  };

  return (
    <div data-lenis-prevent className="h-full min-h-0 space-y-3 overflow-y-auto overscroll-contain p-2.5 pb-8">
      <p className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-300">
        {t('studio.editor.audioBackground')}
      </p>

      <input
        ref={fileInputRef}
        type="file"
        accept="audio/mpeg,audio/mp4,audio/x-m4a,audio/m4a,audio/wav"
        className="hidden"
        onChange={handleFile}
      />

      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/20 bg-white/[0.02] text-xs font-bold text-white/50 transition hover:border-brand-400 hover:bg-brand-500/10 hover:text-brand-300 disabled:opacity-50"
      >
        <Upload className="h-4 w-4" />
        {uploading ? 'Загрузка…' : 'Добавить аудиодорожку'}
        <span className="text-[9px] font-medium text-white/35">MP3 · M4A · WAV</span>
      </button>

      {error && <p className="text-[11px] font-medium text-red-500">{error}</p>}

      {audioTracks.length > 0 && (
        <div className="space-y-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-2">
          <div className="flex items-center justify-between px-1">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-white/40">На таймлайне</p>
            <span className="text-[10px] font-bold text-white/35">{audioTracks.length}</span>
          </div>
          {audioTracks.map((track, index) => (
            <div
              key={track.id ?? `${track.fileName}-${index}`}
              className={`flex items-center gap-2 rounded-lg border p-2 transition ${
                audioTrack?.id === track.id
                  ? 'border-coral-400/60 bg-coral-500/10'
                  : 'border-white/10 bg-black/10 hover:border-white/20'
              }`}
            >
              <button
                type="button"
                onClick={() => track.id && onSelect(track.id)}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-coral-500/15 text-coral-300">
                  <Music className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-black text-white">{track.fileName ?? `Audio ${index + 1}`}</span>
                  <span className="block text-[10px] font-medium text-white/40">
                    {formatSeconds(track.timelineDuration ?? track.duration)} · {Math.round(track.volume * 100)}%
                  </span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => track.id && onRemove(track.id)}
                aria-label="Удалить аудиодорожку"
                className="rounded-full p-1.5 text-white/35 transition hover:bg-red-500/10 hover:text-red-400"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <p className="px-1 pt-1 text-[10px] leading-4 text-white/35">
            Громкость, ducking и fades настраиваются справа для выбранной дорожки.
          </p>
        </div>
      )}

      <audio ref={auditionRef} className="hidden" onEnded={() => setAuditioningId(null)} />

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-300">Библиотека</p>
          <span className="text-[10px] font-bold text-white/35">{MUSIC_LIBRARY.length} треков</span>
        </div>
        <label className="flex h-9 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.04] px-3 focus-within:border-brand-500/70">
          <Search className="h-3.5 w-3.5 shrink-0 text-white/35" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Найти трек или настроение"
            className="min-w-0 flex-1 bg-transparent text-[11px] text-white outline-none placeholder:text-white/30"
          />
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Очистить поиск" className="text-white/35 hover:text-white">
              <X className="h-3 w-3" />
            </button>
          )}
        </label>
        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
          {AUDIO_CATEGORIES.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setCategory(item)}
              className={`shrink-0 rounded-full px-2.5 py-1.5 text-[10px] font-bold transition ${
                category === item ? 'bg-brand-500 text-white' : 'bg-white/[0.06] text-white/50 hover:text-white'
              }`}
            >
              {item}
            </button>
          ))}
        </div>
        <div className="mt-2 space-y-1.5">
          {filteredTracks.map((track) => {
            const isActive = audioTracks.some((item) => item.src === track.src);
            const isAuditioning = auditioningId === track.id;
            return (
              <div
                key={track.id}
                className={`flex items-center gap-2 rounded-lg border p-2 transition ${
                  isActive
                    ? 'border-brand-400/60 bg-brand-500/10'
                    : 'border-white/10 bg-white/[0.02]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleAudition(track)}
                  aria-label={isAuditioning ? 'Остановить прослушивание' : 'Прослушать'}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-white/70 transition hover:bg-white/20 hover:text-white"
                >
                  {isAuditioning ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 translate-x-0.5" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-black text-white">{track.title}</p>
                  <p className="truncate text-[10px] font-medium text-white/40">
                    {track.category} · {track.mood} · {formatSeconds(track.duration)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => applyLibraryTrack(track)}
                  disabled={isActive}
                  className={`flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.08em] transition ${
                    isActive
                      ? 'bg-brand-500/20 text-brand-300'
                      : 'bg-white/10 text-white/70 hover:bg-brand-500 hover:text-white'
                  }`}
                >
                  {isActive ? (
                    <>
                      <Check className="h-3 w-3" /> Используется
                    </>
                  ) : (
                    'Использовать'
                  )}
                </button>
              </div>
            );
          })}
          {filteredTracks.length === 0 && (
            <div className="rounded-xl border border-dashed border-white/10 px-3 py-6 text-center text-[11px] text-white/35">
              Ничего не найдено. Попробуйте другое настроение.
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
